import { useState } from "react";
import axios from "axios";
import {
  Cake,
  CalendarHeart,
  Check,
  Heart,
  Loader2,
  MapPin,
  Pencil,
  Phone,
  User,
  X,
} from "lucide-react";
import { MemberSearchApi } from "@/api/giftcard.api";
import type { MemberProfile } from "../types";
import { getFormattedDateTime } from "@/utils";

type EditableField = "mobile" | "name" | "dob" | "doa" | "gender" | "city";

interface ProfileCardProps {
  member: MemberProfile;
  onSave: (updates: Pick<MemberProfile, EditableField>) => void;
}

const fields: {
  key: EditableField;
  label: string;
  icon: typeof Phone;
  placeholder: string;
  inputType?: string;
}[] = [
  {
    key: "mobile",
    label: "Mobile Number",
    icon: Phone,
    placeholder: "Enter mobile number",
    inputType: "tel",
  },
  {
    key: "name",
    label: "Name",
    icon: User,
    placeholder: "Enter name",
  },
  {
    key: "dob",
    label: "DOB",
    icon: Cake,
    placeholder: "Select date of birth",
    inputType: "date",
  },
  {
    key: "doa",
    label: "DOA",
    icon: CalendarHeart,
    placeholder: "Select date of anniversary",
    inputType: "date",
  },
  {
    key: "gender",
    label: "Gender",
    icon: Heart,
    placeholder: "Enter gender",
  },
  {
    key: "city",
    label: "City/Area",
    icon: MapPin,
    placeholder: "Enter city/area",
  },
];

/** Normalize any date string to `YYYY-MM-DD` for `<input type="date" />`. */
function toDateInputValue(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return "";

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Display date using app standard datetime format. */
function formatDisplayDate(value: string) {
  const ymd = toDateInputValue(value);
  if (!ymd) return value || "—";
  const [year, month, day] = ymd.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return getFormattedDateTime(date) || value || "—";
}

/** Convert `YYYY-MM-DD` to API ISO datetime, e.g. `2026-07-16T00:00:00.000Z`. */
function toApiIsoDate(yyyyMmDd: string) {
  const trimmed = yyyyMmDd.trim();
  if (!trimmed) return "";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toISOString();
  }
  const now = new Date();
  const [year, month, day] = trimmed.split("-").map(Number);
  return new Date(
    Date.UTC(
      year,
      month - 1,
      day,
      now.getUTCHours(),
      now.getUTCMinutes(),
      now.getUTCSeconds(),
      now.getUTCMilliseconds()
    )
  ).toISOString();
}

function getEditableSlice(member: MemberProfile) {
  return {
    mobile: member.mobile,
    name: member.name,
    dob: toDateInputValue(member.dob),
    doa: toDateInputValue(member.doa),
    gender: member.gender,
    city: member.city,
  };
}

function getApiErrorMessage(err: unknown, fallback: string) {
  if (!axios.isAxiosError(err)) return fallback;
  const data = err.response?.data as
    | { error_msg?: string; message?: string }
    | undefined;
  return data?.error_msg || data?.message || fallback;
}

export function ProfileCard({ member, onSave }: ProfileCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState(getEditableSlice(member));

  const startEditing = () => {
    setDraft(getEditableSlice(member));
    setError("");
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setDraft(getEditableSlice(member));
    setError("");
    setIsEditing(false);
  };

  const saveEditing = async () => {
    const updates = {
      mobile: draft.mobile.trim(),
      name: draft.name.trim(),
      dob: draft.dob.trim(),
      doa: draft.doa.trim(),
      gender: draft.gender.trim(),
      city: draft.city.trim(),
    };

    if (updates.mobile.replace(/\D/g, "").length < 10) {
      setError("Please enter a valid 10-digit mobile number.");
      return;
    }
    if (!updates.name) {
      setError("Name is required.");
      return;
    }

    setError("");
    setIsSaving(true);
    try {
      const data = await MemberSearchApi.updateMemberDetails({
        p_MobileNo: updates.mobile.replace(/\D/g, ""),
        p_Name: updates.name,
        p_DOB: toApiIsoDate(updates.dob),
        p_DOA: toApiIsoDate(updates.doa),
        p_Gender: updates.gender,
        p_City: updates.city,
      });

      if (data?.error_msg) {
        setError(data.error_msg);
        return;
      }

      onSave({
        ...updates,
        dob: formatDisplayDate(updates.dob),
        doa: formatDisplayDate(updates.doa),
      });
      setIsEditing(false);
    } catch (err) {
      console.error("UpdateMemberDetails failed:", err);
      setError(
        getApiErrorMessage(err, "Unable to update profile. Please try again.")
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm sm:p-6">
      <div className="mb-6 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold tracking-tight text-slate-900">
            Member Profile
          </h2>
          <p className="mt-1 text-sm text-slate-500">Personal details</p>
        </div>

        {isEditing ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={cancelEditing}
              disabled={isSaving}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50"
            >
              <X className="h-3.5 w-3.5" />
              Cancel
            </button>
            <button
              type="button"
              onClick={saveEditing}
              disabled={isSaving}
              className="inline-flex items-center gap-1.5 rounded-xl bg-slate-900 px-3 py-2 text-xs font-semibold text-white transition hover:bg-slate-800 disabled:opacity-50"
            >
              {isSaving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Check className="h-3.5 w-3.5" />
              )}
              {isSaving ? "Saving…" : "Save"}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={startEditing}
            aria-label="Edit profile"
            className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 shadow-sm transition hover:border-amber-300 hover:bg-amber-50 hover:text-amber-700"
          >
            <Pencil className="h-4 w-4" />
          </button>
        )}
      </div>

      {error ? (
        <p
          role="alert"
          className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
        >
          {error}
        </p>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {fields.map(({ key, label, icon: Icon, placeholder, inputType }) => (
          <div
            key={key}
            className={`flex items-start gap-3 rounded-xl border px-4 py-3.5 transition ${
              isEditing
                ? "border-amber-200 bg-amber-50/40"
                : "border-slate-100 bg-slate-50/50 hover:border-slate-200 hover:bg-white"
            }`}
          >
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white text-slate-500 shadow-sm ring-1 ring-slate-100">
              <Icon className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-400">
                {label}
              </p>
              {isEditing ? (
                key === "gender" ? (
                  <select
                    value={draft.gender}
                    disabled={isSaving}
                    onChange={(e) =>
                      setDraft((prev) => ({ ...prev, gender: e.target.value }))
                    }
                    className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-semibold text-slate-800 outline-none transition focus:border-amber-400 focus:ring-2 focus:ring-amber-100 disabled:opacity-60"
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                    <option value="Prefer not to say">Prefer not to say</option>
                  </select>
                ) : (
                  <input
                    type={inputType ?? "text"}
                    value={draft[key]}
                    disabled={isSaving}
                    onChange={(e) => {
                      const value =
                        key === "mobile"
                          ? e.target.value.replace(/\D/g, "").slice(0, 10)
                          : e.target.value;
                      setDraft((prev) => ({ ...prev, [key]: value }));
                    }}
                    placeholder={placeholder}
                    className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm font-semibold text-slate-800 outline-none transition focus:border-amber-400 focus:ring-2 focus:ring-amber-100 disabled:opacity-60"
                  />
                )
              ) : (
                <p className="mt-0.5 truncate text-sm font-semibold text-slate-800">
                  {key === "dob" || key === "doa"
                    ? formatDisplayDate(member[key])
                    : member[key]}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
